import { Component, Input, OnChanges } from "@angular/core";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { IActivityGroup } from "@org-common/lib/activities/activities-metadata";
import { ActivityPageService } from "@org-common/lib/activities/activity-page.service";
import { CulturalActivityMetadataByType, NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { IMeetingTeamGroup, MeetingsService } from "@org-common/lib/meetings/meetings.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import moment from "moment";
import { debounceTime } from "rxjs/operators";

@Component({
    selector: "adapt-personal-meetings-activity-section",
    templateUrl: "./personal-meetings-activity-section.component.html",
    styleUrls: ["./personal-meetings-activity-section.component.scss"],
    standalone: false,
})
export class PersonalMeetingsActivitySectionComponent extends BaseComponent implements OnChanges {
    public readonly section = CulturalActivityMetadataByType[NonCulturalActivityType.Meetings];
    public readonly editTeamKanban = CommonTeamsAuthService.EditTeamKanban;

    @Input({ required: true }) public activityGroup!: IActivityGroup<Meeting>;
    @Input() public countTooltip?: string;
    @Input() public isLoading = false;
    @Input() public error?: any;
    @Input() public fallbackText?: string;

    public ongoingMeetings?: IMeetingTeamGroup[];
    public upcomingMeetings?: IMeetingTeamGroup[];
    public upcomingMeetingsPastScheduledDate?: IMeetingTeamGroup[];

    public displayedCount = 0;

    public constructor(
        private meetingsService: MeetingsService,
        private activitiesService: ActivitiesService,
        activityPageService: ActivityPageService,
    ) {
        super();

        activityPageService.filterUpdated$.pipe(
            debounceTime(100),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.ngOnChanges());
    }

    public async ngOnChanges() {
        const meetings = this.activityGroup.allItems;

        this.ongoingMeetings = this.meetingsService.groupMeetingsByTeam(meetings.filter((m) => m.extensions.isInProgress));

        // show scheduled in past meetings in another group so that we see more relevant meetings first
        const nonStartedMeetings = meetings.filter((m) => m.extensions.isNotStarted);
        const [upcomingMeetings, upcomingMeetingsPastScheduledDate] = ArrayUtilities.partition(nonStartedMeetings, (m) => moment(m.meetingDateTime).startOf("day").isSameOrAfter(new Date(), "date"));

        // only 3 per team group
        this.upcomingMeetings = this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Upcoming)
            ? this.groupSortTeamMeetings(upcomingMeetings)
            : [];

        this.upcomingMeetingsPastScheduledDate = this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Overdue)
            ? this.groupSortTeamMeetings(upcomingMeetingsPastScheduledDate)
            : [];

        this.displayedCount = this.ongoingMeetings
            .concat(this.upcomingMeetings)
            .concat(this.upcomingMeetingsPastScheduledDate)
            .flatMap((g) => g.meetings)
            .length;
    }

    private groupSortTeamMeetings(meetings: Meeting[]) {
        const groupedMeetings = this.meetingsService.groupMeetingsByTeam(meetings)
            .map((group) => ({
                ...group,
                // show most relevant meetings first
                meetings: SortUtilities.sortArrayClosestDate(group.meetings, (m) => m.meetingDateTime).slice(0, 3),
            }));

        // sort the teams by the soonest meeting
        return SortUtilities.sortArrayClosestDate(groupedMeetings, (g) => g.meetings[0].meetingDateTime);
    }
}
